import { tokenStorage } from './tokenStorage';

/**
 * Token Refresh Utility
 * Handles refreshing user access tokens using refresh tokens
 */

/**
 * Refresh the access token using the stored refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export async function refreshAccessToken() {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
        return null;
    }

    // Determine which client ID to use (you may need to store provider info)
    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID
    const authIssuer = process.env.REACT_APP_AUTH_ISSUER
    const refreshUrl = `${authIssuer}/protocol/openid-connect/token`;

    const params = new URLSearchParams({
        client_id: clientId,
        grant_type: 'refresh_token',
        refresh_token: refreshToken
    });

    try {
        const response = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: params
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('Failed to refresh token:', error);
            // Clear tokens on refresh failure
            tokenStorage.clearTokens();
            return null;
        }

        const refreshed = await response.json();

        // Update stored tokens
        const expiresAt = Math.floor(Date.now() / 1000) + (refreshed.expires_in || 3600);
        tokenStorage.setTokens({
            access_token: refreshed.access_token,
            refresh_token: refreshed.refresh_token || refreshToken,
            id_token: refreshed.id_token || tokenStorage.getIdToken() || '',
            expires_at: expiresAt
        });

        return refreshed.access_token;
    } catch (error) {
        console.error('Error refreshing access token:', error);
        tokenStorage.clearTokens();
        return null;
    }
}

/**
 * Get a valid access token, refreshing if necessary
 * @returns {Promise<string|null>} Valid access token or null if user not authenticated
 */
export async function getValidAccessToken() {
    // Check if we have a token
    let accessToken = tokenStorage.getAccessToken();

    if (!accessToken) {
        return null; // User not authenticated
    }

    // Check if token is expired or expiring soon (within 60 seconds)
    if (tokenStorage.isTokenExpired()) {
        // Try to refresh
        accessToken = await refreshAccessToken();
    } else {
        // Check if expiring soon
        const expiresAt = tokenStorage.getExpiresAt();
        if (expiresAt) {
            const now = Math.floor(Date.now() / 1000);
            if (expiresAt - now < 60) {
                // Token expiring soon, refresh proactively
                accessToken = await refreshAccessToken();
            }
        }
    }

    return accessToken;
}
